import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import AdminSidebar from "../../components/partial/AdminNavbar";
import {
  getDownloadData,
  getOneDownloadData,
  updateDownload,
} from "../../store/download/reducer";
import { SlClose } from "react-icons/sl";
import { CgSpinnerTwoAlt } from "react-icons/cg";

const EditDownload = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { id } = location.state;
  const { title } = useParams();
  const [updatedTitle, setUpdatedTitle] = useState(title);
  const [updatedDescription, setUpdatedDescription] = useState("");
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [removedFiles, setRemovedFiles] = useState([]);
  const [isUpdated, setIsUpdated] = useState(false);
  const [loading, setLoading] = useState(false);

  const dispatch = useDispatch();
  const OneDownloadData = useSelector(
    (state) => state.downloadReducer.OneDownloadData
  );

  useEffect(() => {
    if (OneDownloadData) {
      setUpdatedTitle(OneDownloadData.title || "");
      setUpdatedDescription(OneDownloadData.description || "");
      setUploadedFiles(
        Array.isArray(OneDownloadData.files) ? OneDownloadData.files : []
      );
    }
  }, [OneDownloadData, title]);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      setUploadedFiles([...uploadedFiles, selectedFile]);
      // { file: selectedFile, filename: selectedFile.name }
    }
  };

  const handleFileDelete = (fileToDelete) => {
    setUploadedFiles(uploadedFiles.filter((file) => file !== fileToDelete));
    setRemovedFiles([...removedFiles, fileToDelete]);
  };

  useEffect(() => {
    setIsUpdated(
      updatedTitle !== OneDownloadData?.title ||
        updatedDescription !== OneDownloadData?.description ||
        uploadedFiles.length > 0 ||
        removedFiles.length > 0
    );
  }, [
    updatedTitle,
    updatedDescription,
    uploadedFiles,
    removedFiles,
    OneDownloadData,
  ]);

  useEffect(() => {
    dispatch(getOneDownloadData(id));
  }, [dispatch, id]);

  const handleUpdate = () => {
    setLoading(true);
    if (isUpdated) {
      const formData = new FormData();
      formData.append("title", updatedTitle);
      formData.append("description", updatedDescription);

      uploadedFiles.forEach((file) => {
        formData.append("file", file); // Uploading new files
      });

      // Send removed files as a JSON array of file IDs
      formData.append(
        "filesToDelete",
        JSON.stringify(removedFiles.map((file) => file._id)) // Send IDs of files to delete
      );

      // Dispatch the update action
      dispatch(updateDownload(id, formData))
        .then(() => {
          // Perform subsequent actions after the update
          getDownloadData(dispatch);
          setUpdatedTitle(""); // Clear title
          setUpdatedDescription(""); // Clear description
          setUploadedFiles([]); // Reset uploaded files
          setRemovedFiles([]); // Reset removed files
          setIsUpdated([]); // Reset update status
        })
        .catch((error) => {
          console.error("Error updating download:", error);
        })
        .finally(setLoading(false), navigate("/nvm-admin/download"));
    }
  };

  return (
    <div className="flex flex-col md:flex-row md:items-center bg-gray-100 min-h-screen">
      {/* Sidebar */}
      <div className="lg:w-1/4 md:w-1/3 bg-white shadow-md">
        <AdminSidebar />
      </div>

      {/* Main Content */}
      <div className="flex-1 p-6 md:flex-row">
        <h2 className="text-2xl lg:text-3xl font-bold text-gray-800 text-center mb-6">
          {title}
        </h2>

        <div className="bg-white shadow-md rounded-lg p-6 max-w-3xl mx-auto">
          <div className="mb-4">
            <label className="block text-gray-700 font-semibold mb-2">
              Edit Title
            </label>
            <input
              type="text"
              value={updatedTitle}
              onChange={(e) => setUpdatedTitle(e.target.value)}
              className="w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Enter new title"
            />
          </div>

          <div className="mb-4">
            <label className="block text-gray-700 font-semibold mb-2">
              Edit Description
            </label>
            <textarea
              value={updatedDescription}
              onChange={(e) => setUpdatedDescription(e.target.value)}
              className="w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Enter description"
              rows="4"
            />
          </div>
          <div className="mb-4">
            <label className="block text-gray-700 font-semibold mb-2">
              Upload Files
            </label>
            <input
              type="file"
              onChange={handleFileChange}
              accept=".doc,.docx,.txt,.pdf"
              className="w-full p-3 border rounded-lg bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div className="flex justify-end mt-6">
            {loading ? (
              <button
                disabled
                className={`px-6 py-3 rounded-lg font-semibold flex items-center justify-center bg-blue-500 text-white`}
              >
                <CgSpinnerTwoAlt className="text-black animate-spin text-3xl duration-300 " />{" "}
                <span className="pl-1">Please wait</span>
              </button>
            ) : (
              <button
                onClick={handleUpdate}
                disabled={!isUpdated}
                className={`px-6 py-3 rounded-lg font-semibold text-white transition ${
                  isUpdated
                    ? "bg-blue-500 hover:bg-blue-600"
                    : "bg-gray-400 cursor-not-allowed"
                }`}
              >
                Update
              </button>
            )}
          </div>
          <div className="cursor-pointer mt-4 flex flex-col items-center justify-center w-full space-y-2">
            {/* Display file name */}
            {uploadedFiles.map((file, index) => (
              <div
                className="border-2 shadow-lg p-2 w-2/3 rounded-md flex justify-between items-center hover:scale-105 duration-300"
                key={index}
              >
                <div>
                  {file.filename || file.name
                    ? (file.filename || file.name)
                        .split(".") // Split by dot to separate the extension
                        .slice(0, -1) // Remove the last part (file extension)
                        .join(".") // Join back if needed (for cases like multiple dots in the name)
                        .replace(/%20/g, " ") // Replace "%20" with spaces
                        .replace(/_20/g, " ") // Replace "_20" with spaces
                    : ""}
                </div>
                <div>
                  <SlClose
                    onClick={() => handleFileDelete(file)}
                    className="text-xl text-red-600 hover:scale-110 duration-300"
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditDownload;
